import React, { JSX, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { getTranslateByKey } from '@/main/services/utils';
import * as S from './styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiPaper-root': {
        padding: '1rem 1rem 1rem !important '
      }
    },
    actions: {
      padding: '1rem 0 0'
    },
    paper: {
      padding: '0'
    }
  })
);

interface IAsyncModalConfirmProps {
  closeByOverlay?: boolean;
  confirmLabelKey?: string;
  cancelLabelKey?: string;
}

const createPromise = (): [
  Promise<boolean>,
  (value: boolean | PromiseLike<boolean>) => void
] => {
  let resolver: (value: boolean | PromiseLike<boolean>) => void;
  const promise = new Promise<boolean>(resolve => {
    resolver = resolve;
  });
  return [promise, resolver!];
};

const useAsyncModalConfirm = (): [
  (text: string | JSX.Element) => Promise<boolean>,
  (props: IAsyncModalConfirmProps) => JSX.Element
] => {
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [resolver, setResolver] = useState<
    ((value: boolean | PromiseLike<boolean>) => void) | null
  >(null);
  const [label, setLabel] = useState<string>('');

  const getConfirmation = (text: string): Promise<boolean> => {
    setLabel(text);
    setOpen(true);
    const [promise, resolve] = createPromise();
    setResolver(() => resolve);
    return promise;
  };

  function handleClickNo(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    setOpen(false);
    if (resolver) {
      resolver(false);
    }
  }
  function handleClickYes(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    setOpen(false);

    if (resolver) {
      resolver(true);
    }
  }

  const ModalConfirmation = ({
    closeByOverlay = false,
    confirmLabelKey = "information.yes",
    cancelLabelKey = "information.no"
  }: IAsyncModalConfirmProps): JSX.Element => {
    const handleClose = (
      event: {},
      reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
      if (closeByOverlay) {
        setOpen(false);
      }
    };

    return (
      <Dialog open={open} className={classes.root} onClose={handleClose}>
        <DialogContent className={classes.paper}>
          <S.Text>{label}</S.Text>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <S.AreaFooterButtons>
            <SecondaryButton id="id-btn-no" onClick={handleClickNo}>
              {getTranslateByKey(cancelLabelKey)}
            </SecondaryButton>
            <Button id="id-btn-yes" onClick={handleClickYes}>
              {getTranslateByKey(confirmLabelKey)}
            </Button>
          </S.AreaFooterButtons>
        </DialogActions>
      </Dialog>
    );
  };

  return [getConfirmation, ModalConfirmation];
};

export default useAsyncModalConfirm;
